import React from "react";

import classnames from "classnames";
import EmployeeApplicationApp from "EmployeeApplication/EmployeeApplicationApp";
import { ModularEmployerApp } from "EmployerApplication/App";
import { AddDentalModal } from "EmployerApplication/Components/shoppingCartSection/AddDentalModal";
import { AddVisionModal } from "EmployerApplication/Components/shoppingCartSection/AddVisionModal";
import EmployerRenewalApplication from "EmployerApplication/EmployerRenewalApplication";
import { nestedPrefixRoutes, baseURL as employerApplicationBaseURL } from "EmployerApplication/urls";
import ConciergeChat from "Features/ConciergeChat/ConciergeChat";
import isIntuit from "Intuit/isIntuit";
import CensusProcessedModal from "QuotePage/app/CensusProcessedModal";
import { showOverlay } from "QuotePage/Selectors";
import { baseName, isRenewal } from "QuotePage/urls";
import { connect, useDispatch } from "react-redux";
import { Router, Route, Switch } from "react-router-dom";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import PageViewTracker from "ZeroRuby/PageViewTracker";
import Heartbeat from "ZeroRuby/snippets/heartbeat";
import { OptimizeActivate } from "ZeroRuby/snippets/optimize";

import { useModal } from "Cobalt/Modal";
import EnterAgesModal from "Common/EnterAges/Components/EnterAgesModal";
import Footer from "Common/Footer";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import PartnerProvider from "Common/PartnerProvider";
import PriceBreakdown from "Common/PriceBreakdown";
import { SizeSwitch } from "Common/Responsive";
import ThemeInstaller from "Common/ThemeInstaller";

import BlankQuotePage from "./app/BlankQuotePage";
import CompactQuotePage from "./app/CompactQuotePage";
import ComparePlansPage from "./app/ComparePage";
import ConsultPlanRecommendationModal from "./app/ConsultPlanRecommendationModal";
import ContactUsModal from "./app/ContactUsModal";
import EffectiveDateChanger from "./app/EffectiveDateChanger";
import MobileComparePlansPage from "./app/MobileComparePage";
import PlanDetails from "./app/PlanDetails";
import QuotePageCartModal from "./app/QuotePageCartModal";
import QuotePageTopBar from "./app/QuotePageTopBar";
import QuotePageContext from "./Context";
import { history } from "./Store";

import makeBem from "Utils/makeBem";

const ANALYTICS_LOCATION = "quote_page";

type Props = {
  showOverlayProp: boolean;
  parsedCensus?: {
    employee_profiles: Array<any>;
    employees_missing_ages: Array<any>;
    terminated_employees: Array<any>;
    error: string | null;
  };
};

type SelectedQuotePageProps = {
  showOverlayProp: boolean;
};

export const SelectedQuotePageApp = ({ showOverlayProp }: SelectedQuotePageProps) => {
  if (showOverlayProp) {
    return <BlankQuotePage />;
  }
  return <CompactQuotePage />;
};

export const AppDumb = ({ showOverlayProp, parsedCensus }: Props) => {
  const dispatch = useDispatch();
  const { isShowing: isDentalModalShowing, show: showDentalModal, hide: hideDentalModal } = useModal();

  const { isShowing: isVisionModalShowing, show: showVisionModal, hide: hideVisionModal } = useModal();

  const handleShowEnterAgesModal = () => {
    AnalyticsTracking.trackEvent("enter_ages_modal_shown", {
      location: ANALYTICS_LOCATION,
    });
  };

  const handleHideEnterAgesModal = () => {
    AnalyticsTracking.trackEvent("enter_ages_modal_hidden", {
      location: ANALYTICS_LOCATION,
    });
  };

  const handleShowContactUsModal = () => {
    AnalyticsTracking.trackEvent("contact_us_modal_shown", {
      location: ANALYTICS_LOCATION,
    });
  };

  const handleHideContactUsModal = () => {
    AnalyticsTracking.trackEvent("contact_us_modal_hidden", {
      location: ANALYTICS_LOCATION,
    });
  };

  const bem = makeBem("QuotePage");
  return (
    <Router history={history}>
      <PageViewTracker>
        <PartnerProvider>
          <QuotePageContext.Provider
            value={{
              isDentalModalShowing,
              showDentalModal,
              isVisionModalShowing,
              showVisionModal,
            }}
          >
            <div
              className={classnames(bem(), {
                [bem(null, "intuit")]: isIntuit(),
              })}
            >
              <QuotePageTopBar />
              <Switch>
                <Route path="/**/select-plan" children={() => !isRenewal() && <EffectiveDateChanger />} />
              </Switch>
              <EnterAgesModal onShow={handleShowEnterAgesModal} onHide={handleHideEnterAgesModal} />

              <ContactUsModal onShow={handleShowContactUsModal} onHide={handleHideContactUsModal} />

              <AddDentalModal show={isDentalModalShowing} onHide={hideDentalModal} />

              <AddVisionModal show={isVisionModalShowing} onHide={hideVisionModal} />

              <CensusProcessedModal parsedCensus={parsedCensus} />

              <ConsultPlanRecommendationModal />
              <Div className="min-h-dvh">
                <Switch>
                  <Route
                    // Multiple path support for <Route /> will go away in v6, hooks will be used instead.
                    path={["/**/select-plan", "/quickbooks/authenticate", "/quickbooks/oauth_callback", "/primepay/connect"]}
                    children={<SelectedQuotePageApp showOverlayProp={showOverlayProp} />}
                  />

                  <Route
                    path={nestedPrefixRoutes("/employer-application/company-admin/employee-application/:employee_hash_id")}
                    children={({ match }) => <EmployeeApplicationApp match={match} />}
                  />

                  <Route path={nestedPrefixRoutes("/employer-application")} children={<ModularEmployerApp />} />
                  <Route path={nestedPrefixRoutes("/employer-renewal")} children={<EmployerRenewalApplication />} />
                </Switch>
                {/* Routes that render at the same time as other Routes, wrapped in their own Switch for the v5/v6 upgrade */}
                <Switch>
                  <Route path="/(.+)+/compare-plans" children={<SizeSwitch xs={<MobileComparePlansPage />} sm={<ComparePlansPage />} />} />
                </Switch>
                <Switch>
                  <Route exact path="/(.+)+/plan-details/:id" children={<PlanDetails />} />
                </Switch>
                <Switch>
                  <Route exact path="/(.+)+/price-breakdown/:id" children={<PriceBreakdown />} />
                </Switch>
                <Switch>
                  <Route exact path="/(.+)+/cart" children={<QuotePageCartModal />} />
                </Switch>
                <Route
                  path={[...nestedPrefixRoutes(baseName), ...nestedPrefixRoutes(employerApplicationBaseURL)]}
                  children={
                    <ConciergeChat
                      title={
                        <>
                          <Icon icon="comment-dots" /> SimplyInsured Agent
                        </>
                      }
                      history={history}
                      dispatch={dispatch}
                    />
                  }
                />
              </Div>
              <Footer />
              <Heartbeat />
              <ThemeInstaller />
              <OptimizeActivate />
            </div>
          </QuotePageContext.Provider>
        </PartnerProvider>
      </PageViewTracker>
    </Router>
  );
};

const mapStateToProps = (state) => ({
  showOverlayProp: showOverlay(state),
  parsedCensus: state.companyProfile?.parsed_census,
});

const QuotePageApp = connect(mapStateToProps)(AppDumb);

export default QuotePageApp;
